import React, { useMemo } from "react"
import { Activity, TrendingUp, TrendingDown, Lightbulb, BarChart3, DollarSign } from "lucide-react"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts"
import { ReportCard, ReportSectionHeader, ReportStatTile, ReportDelta, ReportEmptyState, ReportBadge } from "../components/ReportUI"
import { REPORT_COLORS } from "../components/reportTheme"
import { toArray, formatNumbersInText, fit } from "../components/reportUtils"
import { isBlurredField, badgeAware, BlurredInline, BlurredBlock, BlurredContentFade, BlurredStaticLines, partitionBlurred } from "../components/BlurredField"

const fmt = d => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })
const fmtNum = (n, size) => {
  if (isBlurredField(n)) return <BlurredInline field={n} size={size} />
  return n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : Math.round(n || 0).toLocaleString()
}
const fmtCurrency = (n, size) => {
  if (isBlurredField(n)) return <BlurredInline field={n} size={size} />
  return n < 0 ? `-$${fmtNum(-n)}` : `$${fmtNum(n)}`
}

const ChartTooltip = ({ active, payload, label, isCurrency }) => {
  if (!active || !payload?.length) return null
  const point = payload.find(p => p.value != null)
  if (!point) return null
  return (
    <div style={{ backgroundColor: REPORT_COLORS.cardBgRaised, border: `1px solid ${REPORT_COLORS.cardBorder}`, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
      <div style={{ color: REPORT_COLORS.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ color: REPORT_COLORS.textPrimary, fontWeight: 600, fontFamily: "monospace" }}>
        {isCurrency ? fmtCurrency(point.value) : fmtNum(point.value)}
      </div>
    </div>
  )
}

// Field names differ from the authenticated /forecasting/forecast/ endpoint
// this tab's sibling reads elsewhere — the public snapshot comes from
// /forecasting/forecast-temp/ instead, so history/forecast arrays are read
// under either naming, and each series' value lives under its own key
// (recent_downloads → value, forecast → downloads, revenue_forecast → revenue).
const seriesOf = (data, ...keys) => {
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key]
  }
  return []
}

const valueOf = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] != null) return item[key]
  }
  return undefined
}

const rangeOf = range => {
  if (!range || isBlurredField(range)) return null
  const low = range.low ?? range.min ?? range.lower
  const high = range.high ?? range.max ?? range.upper
  if (low == null && high == null) return null
  return { low, high }
}

const GROWTH_DOWN_PATTERN = /declin|decreas|drop|down|slow/i

export default function PublicForecastTab({ section, appName }) {
  const dataBlurred = isBlurredField(section?.data) ? section.data : null
  const data = dataBlurred ? {} : section?.data || {}
  const history = seriesOf(data, "recent_downloads", "actual_downloads")
  const downloadsForecast = seriesOf(data, "forecast", "downloads_forecast")
  const revenueHistory = seriesOf(data, "recent_revenue", "actual_revenue")
  const revenueForecast = seriesOf(data, "revenue_forecast")
  const suggestions = toArray(data.suggestions)
  const insights = toArray(data.insights)
  const downloadsRange = rangeOf(data.downloads_range)
  const revenueRange = rangeOf(data.revenue_range)

  const upliftBlurred = isBlurredField(data.uplift) ? data.uplift : null
  const downloadsUplift = upliftBlurred
    ? null
    : typeof data.uplift === "number"
      ? data.uplift
      : typeof data.uplift?.downloads === "number" || isBlurredField(data.uplift?.downloads)
        ? data.uplift.downloads
        : null
  const revenueUplift = upliftBlurred
    ? null
    : typeof data.uplift?.revenue === "number" || isBlurredField(data.uplift?.revenue)
      ? data.uplift.revenue
      : null

  const growthStatus = data.growth_status
  const growthStatusBlurred = isBlurredField(growthStatus)

  const dlChart = useSeriesChart(history, downloadsForecast, ["value", "downloads"], ["downloads", "value"])
  const revChart = useSeriesChart(revenueHistory, revenueForecast, ["value", "revenue"], ["revenue", "value"])

  if (!section?.available) {
    return (
      <ReportEmptyState
        icon={Activity}
        title="Forecast not available"
        description="Forecast data hasn't been generated for this app yet."
      />
    )
  }

  if (dataBlurred) {
    return <BlurredBlock field={dataBlurred} icon={Activity} title="Forecast" />
  }

  const lastDl = valueOf(history[history.length - 1], "value", "downloads") || 0
  const forecastEndDl = valueOf(downloadsForecast[downloadsForecast.length - 1], "downloads", "value") || 0
  const dlGrowth = lastDl > 0 ? ((forecastEndDl - lastDl) / lastDl) * 100 : 0
  const forecastEndRev = valueOf(revenueForecast[revenueForecast.length - 1], "revenue", "value") || 0

  const growthTone = !growthStatusBlurred && GROWTH_DOWN_PATTERN.test(growthStatus || "") ? "critical" : "good"

  if (!history.length && !downloadsForecast.length) {
    return (
      <ReportEmptyState
        icon={Activity}
        title="No Forecast Data Available"
        description="Forecast data hasn't been generated for this app yet."
      />
    )
  }

  return (
    <div>
      <ReportSectionHeader
        icon={Activity}
        eyebrow="Forecast"
        title={appName ? `${appName} — 7-Day Forecast` : "7-Day Forecast"}
        action={growthStatus ? badgeAware(growthStatus, v => <ReportBadge tone={growthTone}>{v}</ReportBadge>) : null}
      />

      {/* Suggestions and Key Insights lead the tab — there's always
          something here, unlike the forecast charts/metrics below which can
          be empty for a newer app, so the content that's guaranteed to be
          useful comes first. */}
      {(suggestions.length > 0 || insights.length > 0) && (() => {
        // One shared card, one shared Unlock — Suggestions and Key Insights
        // can each independently have locked entries.
        const { visible: visibleSuggestions, locked: lockedSuggestions } = partitionBlurred(suggestions.slice(0, 3))
        const hasSuggestionSubFieldLock = s => isBlurredField(s?.metric) || isBlurredField(s?.issue) || isBlurredField(s?.action) || isBlurredField(s?.impact)
        const suggestionsLocked = lockedSuggestions.length + visibleSuggestions.filter(hasSuggestionSubFieldLock).length

        const { visible: visibleInsights, locked: lockedInsights } = partitionBlurred(insights.slice(0, 3))
        const insightsLocked = lockedInsights.length + visibleInsights.filter(s => isBlurredField(s?.text)).length

        const anyLocked = suggestionsLocked > 0 || insightsLocked > 0
        const hasBoth = suggestions.length > 0 && insights.length > 0

        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              {suggestions.length > 0 && (
                <div style={{ flex: "1 1 240px", minWidth: 0, borderRight: hasBoth ? `1px solid ${REPORT_COLORS.cardBorder}` : "none", paddingRight: hasBoth ? 20 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <Lightbulb size={14} color={REPORT_COLORS.warning} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Suggestions</span>
                  </div>
                  {visibleSuggestions.length === 0 ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    visibleSuggestions.map((s, i) => (
                      <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < visibleSuggestions.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                        {s?.metric && !isBlurredField(s.metric) && <strong style={{ color: REPORT_COLORS.textPrimary }}>{s.metric}: </strong>}
                        {typeof s === "string" ? formatNumbersInText(s) : <>{!isBlurredField(s?.issue) && formatNumbersInText(s?.issue)} — {!isBlurredField(s?.action) && formatNumbersInText(s?.action)}</>}
                        {s?.impact && !isBlurredField(s.impact) && <div style={{ color: REPORT_COLORS.good, marginTop: 2 }}>{formatNumbersInText(s.impact)}</div>}
                      </div>
                    ))
                  )}
                </div>
              )}
              {insights.length > 0 && (
                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    {dlGrowth >= 0 ? <TrendingUp size={14} color={REPORT_COLORS.good} /> : <TrendingDown size={14} color={REPORT_COLORS.critical} />}
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Key Insights</span>
                  </div>
                  {visibleInsights.length === 0 ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    visibleInsights.map((s, i) => (
                      <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8 }}>
                        {typeof s === "string" ? formatNumbersInText(s) : (!isBlurredField(s?.text) && s?.text ? formatNumbersInText(s.text) : JSON.stringify(s))}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {anyLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {(() => {
        // Downloads Forecast and Revenue Forecast are one shared card (two
        // columns, vertical divider on desktop) with exactly ONE Unlock
        // below both — same merged-card + single-CTA pattern as the AI
        // Estimates pair and the forecast charts above, instead of the old
        // per-tile inline "Unlock" chip sitting inside an otherwise-empty
        // stat box.
        const dlValueLocked = isBlurredField(forecastEndDl)
        const revValueLocked = isBlurredField(forecastEndRev)
        const upliftLocked = !!upliftBlurred || isBlurredField(downloadsUplift) || isBlurredField(revenueUplift)
        const explanationLocked = isBlurredField(data.range_explanation)
        // ONE Unlock for the whole forecast-metrics area — Downloads/Revenue
        // Forecast, Uplift, and the range explanation all fold into this
        // single card-level CTA instead of each having its own, which
        // previously left an orphan "Unlock" button floating under an
        // empty Uplift grid whenever only Uplift (not the forecast values
        // themselves) was locked.
        const forecastLocked = dlValueLocked || revValueLocked || upliftLocked || explanationLocked
        // The Uplift row itself is only rendered when there's real,
        // unlocked content to show — a locked Uplift contributes to the
        // Unlock above and otherwise renders nothing here at all.
        const hasUpliftRow = (!upliftLocked && (downloadsUplift != null || revenueUplift != null)) ||
          (!explanationLocked && (downloadsRange || revenueRange) && data.range_explanation)

        return (
          <>
            <ReportCard style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
                <div style={{ flex: "1 1 200px", minWidth: 0, borderRight: `1px solid ${REPORT_COLORS.cardBorder}`, paddingRight: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: REPORT_COLORS.textMuted, marginBottom: 8 }}>
                    Downloads Forecast
                  </div>
                  {dlValueLocked ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    <>
                      <div style={{ fontSize: "clamp(18px, 6vw, 26px)", fontWeight: 700, color: REPORT_COLORS.textPrimary, fontFamily: "monospace", marginBottom: lastDl > 0 ? 6 : 0 }}>
                        {fmtNum(forecastEndDl, "stat")}
                      </div>
                      {lastDl > 0 && <ReportDelta direction={dlGrowth >= 0 ? "up" : "down"} value={`${dlGrowth > 0 ? "+" : ""}${dlGrowth.toFixed(1)}%`} />}
                      <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginTop: 6 }}>
                        {downloadsRange ? `${fmtNum(downloadsRange.low)} – ${fmtNum(downloadsRange.high)}` : "vs. current"}
                      </div>
                    </>
                  )}
                </div>
                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: REPORT_COLORS.textMuted, marginBottom: 8 }}>
                    Revenue Forecast
                  </div>
                  {revValueLocked ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    <>
                      <div style={{ fontSize: "clamp(18px, 6vw, 26px)", fontWeight: 700, color: REPORT_COLORS.textPrimary, fontFamily: "monospace", marginBottom: revenueRange ? 6 : 0 }}>
                        {fmtCurrency(forecastEndRev, "stat")}
                      </div>
                      {revenueRange && (
                        <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted }}>
                          {fmtCurrency(revenueRange.low)} – {fmtCurrency(revenueRange.high)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              {forecastLocked && (
                <div style={{ marginTop: 16, textAlign: "center" }}>
                  <BlurredContentFade items={[]} />
                </div>
              )}
            </ReportCard>

            {hasUpliftRow && (
              <div style={{ marginBottom: 16 }}>
                {(downloadsUplift != null || revenueUplift != null) && (
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, ${fit(160)})`, gap: 12 }}>
                    {downloadsUplift != null && <ReportStatTile label="Download Uplift" value={`+${downloadsUplift}%`} sub="if optimized" />}
                    {revenueUplift != null && <ReportStatTile label="Revenue Uplift" value={`+${revenueUplift}%`} sub="if optimized" />}
                  </div>
                )}
                {data.range_explanation && (
                  <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginTop: 8 }}>{formatNumbersInText(data.range_explanation)}</div>
                )}
              </div>
            )}
          </>
        )
      })()}

      {(() => {
        // Downloads and Revenue forecast charts are one shared card (two
        // columns, vertical divider on desktop) with exactly ONE Unlock
        // below both when either side's underlying series is locked —
        // same merged-card + single-CTA pattern as the AI Estimates pair
        // on the AI CMO tab, instead of two separately-gated full-width
        // cards stacked on top of each other.
        // A whole-field lock (the entire forecast array replaced by one
        // {blurred:true} descriptor) isn't the only shape this takes — the
        // array can come through intact with individual points' own value
        // locked instead, which otherwise breaks the line silently (no
        // visible series) and only reveals itself as an "Unlock" chip in
        // the hover tooltip. Both shapes get the same blurred-placeholder
        // treatment instead of a chart that looks broken.
        const hasLockedPoint = (arr, keys) => arr.some(item => keys.some(k => isBlurredField(item?.[k])))
        const downloadsForecastLocked = isBlurredField(data.forecast) || isBlurredField(data.downloads_forecast) ||
          hasLockedPoint(downloadsForecast, ["downloads", "value"]) || hasLockedPoint(history, ["value", "downloads"])
        const revenueForecastLocked = isBlurredField(data.revenue_forecast) ||
          hasLockedPoint(revenueForecast, ["revenue", "value"]) || hasLockedPoint(revenueHistory, ["value", "revenue"])
        const showDlColumn = dlChart.length > 0 || downloadsForecastLocked
        const showRevColumn = revChart.length > 0 || revenueForecastLocked
        const anyLocked = downloadsForecastLocked || revenueForecastLocked
        const hasBoth = showDlColumn && showRevColumn

        if (!showDlColumn && !showRevColumn) return null

        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              {showDlColumn && (
                <div style={{ flex: "1 1 280px", minWidth: 0, borderRight: hasBoth ? `1px solid ${REPORT_COLORS.cardBorder}` : "none", paddingRight: hasBoth ? 20 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <BarChart3 size={14} color={REPORT_COLORS.textMuted} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Downloads — Recent vs. Forecast</span>
                  </div>
                  {downloadsForecastLocked ? (
                    <BlurredStaticLines lines={3} />
                  ) : (
                    <div style={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={dlChart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="publicReportDlGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={REPORT_COLORS.sequential[400]} stopOpacity={0.35} />
                              <stop offset="100%" stopColor={REPORT_COLORS.sequential[400]} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                          <XAxis dataKey="date" tick={{ fill: REPORT_COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                          <YAxis tick={{ fill: REPORT_COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} width={44} tickFormatter={fmtNum} />
                          <Tooltip content={<ChartTooltip />} />
                          <Area type="monotone" dataKey="actual" stroke={REPORT_COLORS.sequential[400]} strokeWidth={2} fill="url(#publicReportDlGrad)" dot={false} connectNulls={false} />
                          <Area type="monotone" dataKey="forecast" stroke={REPORT_COLORS.sequential[400]} strokeWidth={2} strokeDasharray="5 4" fill="url(#publicReportDlGrad)" dot={{ r: 3, fill: REPORT_COLORS.sequential[400] }} connectNulls={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}

              {showRevColumn && (
                <div style={{ flex: "1 1 280px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <DollarSign size={14} color={REPORT_COLORS.textMuted} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Revenue — Recent vs. Forecast</span>
                  </div>
                  {revenueForecastLocked ? (
                    <BlurredStaticLines lines={3} />
                  ) : (
                    <div style={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revChart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="publicReportRevGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={REPORT_COLORS.good} stopOpacity={0.35} />
                              <stop offset="100%" stopColor={REPORT_COLORS.good} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                          <XAxis dataKey="date" tick={{ fill: REPORT_COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                          <YAxis tick={{ fill: REPORT_COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} width={44} tickFormatter={fmtNum} />
                          <Tooltip content={<ChartTooltip isCurrency />} />
                          <Area type="monotone" dataKey="actual" stroke={REPORT_COLORS.good} strokeWidth={2} fill="url(#publicReportRevGrad)" dot={false} connectNulls={false} />
                          <Area type="monotone" dataKey="forecast" stroke={REPORT_COLORS.good} strokeWidth={2} strokeDasharray="5 4" fill="url(#publicReportRevGrad)" dot={{ r: 3, fill: REPORT_COLORS.good }} connectNulls={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}
            </div>
            {anyLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}
    </div>
  )
}

function useSeriesChart(history, forecast, historyKeys, forecastKeys) {
  return useMemo(() => {
    const recent = history.slice(-30)
    const last = recent[recent.length - 1]
    const lastValue = last ? valueOf(last, ...historyKeys) : null
    return [
      ...recent.map(d => ({ date: fmt(d.date), actual: valueOf(d, ...historyKeys), forecast: undefined })),
      ...(last ? [{ date: fmt(last.date), actual: lastValue, forecast: lastValue }] : []),
      ...forecast.map(d => ({ date: fmt(d.date), actual: undefined, forecast: valueOf(d, ...forecastKeys) })),
    ]
  }, [history, forecast, historyKeys, forecastKeys])
}
